import { formatAppointmentDateOnlyInClinic } from './clinicTime';
import {
  MARITAL_STATUS_OPTIONS,
  EDUCATION_OPTIONS,
  EVOLUTION_UNITS,
  PROGNOSIS_OPTIONS,
  FOOTWEAR_HABIT_OPTIONS,
  FOOT_HYGIENE_OPTIONS,
  CARDIOVASCULAR_CONDITIONS,
  ENDOCRINE_CONDITIONS,
  DERMATOLOGICAL_CONDITIONS,
  NEUROLOGICAL_CONDITIONS,
  GASTROINTESTINAL_CONDITIONS,
  TEMPERATURE_OPTIONS,
  EDEMA_OPTIONS,
  GAIT_TYPE_OPTIONS,
  PLANTAR_PRINT_OPTIONS,
  SENSITIVITY_ROWS,
  ALTERATION_ZONES,
  REFLEX_OPTIONS,
  LIMITATION_OPTIONS,
  ADULT_SYMPTOM_OPTIONS,
} from './clinicalIntakeSchema';

export const INTAKE_RECORD_PREVIEW_STORAGE_KEY = 'podoclinic_intake_record_preview_v1';

const DASH = '—';

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Atributo src/href: no romper URLs con & ni data: */
function escapeAttr(s) {
  return String(s || '').replace(/"/g, '&quot;');
}

function formatDate(ymd) {
  const raw = String(ymd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || '';
  try {
    return formatAppointmentDateOnlyInClinic(raw, "d 'de' MMMM 'de' yyyy");
  } catch {
    return raw;
  }
}

function clean(value) {
  return String(value == null ? '' : value).trim();
}

function labelFromOptions(options, value) {
  const v = clean(value);
  if (!v) return '';
  const found = options.find((o) => String(o.value) === v || String(o.id) === v);
  return found ? found.label : v;
}

function boolLabel(value) {
  return value ? 'Sí' : 'No';
}

/** Convierte un valor a celda legible (— si vacío). */
function val(value) {
  const v = clean(value);
  return v ? escapeHtml(v) : DASH;
}

function multilineVal(value) {
  const v = clean(value);
  return v ? escapeHtml(v).replace(/\n/g, '<br />') : DASH;
}

/** Tabla de pares etiqueta/valor. rows = [[label, value], ...] */
function fieldTable(rows) {
  const body = rows
    .map(
      ([label, value]) => `
      <tr>
        <th class="field-label">${escapeHtml(label)}</th>
        <td class="field-value">${value}</td>
      </tr>`
    )
    .join('');
  return `<table class="field-table"><tbody>${body}</tbody></table>`;
}

function section(title, inner) {
  return `
    <section class="doc-section">
      <h2 class="section-title">${escapeHtml(title)}</h2>
      ${inner}
    </section>`;
}

function subTitle(text) {
  return `<h3 class="sub-title">${escapeHtml(text)}</h3>`;
}

/** Bloque de antecedente patológico/no patológico/heredofamiliar. */
function pathologyRow(label, block, conditionOptions = []) {
  const b = block || {};
  const checked = !!b.checked;
  const conditions = Array.isArray(b.conditions) ? b.conditions : [];
  const condLabels = conditions
    .map((c) => labelFromOptions(conditionOptions, c) || c)
    .filter(Boolean)
    .join(', ');
  const detail = clean(b.detail);
  const evolution = clean(b.evolution_time);
  const treatment = clean(b.treatment);
  const controlled = clean(b.controlled);

  if (!checked && !condLabels && !detail && !evolution && !treatment && !controlled) {
    return `
      <tr>
        <th class="field-label">${escapeHtml(label)}</th>
        <td class="field-value">${DASH}</td>
      </tr>`;
  }

  const parts = [];
  if (condLabels) parts.push(escapeHtml(condLabels));
  if (detail) parts.push(escapeHtml(detail));
  const extra = [];
  if (evolution) extra.push(`Evolución: ${escapeHtml(evolution)}`);
  if (treatment) extra.push(`Tratamiento: ${escapeHtml(treatment)}`);
  if (controlled) extra.push(`Control: ${escapeHtml(controlled)}`);
  let content = parts.join(' — ');
  if (!content) content = checked ? 'Sí' : DASH;
  if (extra.length) content += `<br /><span class="muted">${extra.join(' · ')}</span>`;

  return `
    <tr>
      <th class="field-label">${escapeHtml(label)}</th>
      <td class="field-value">${content}</td>
    </tr>`;
}

function pathologyTable(rows) {
  return `<table class="field-table"><tbody>${rows.join('')}</tbody></table>`;
}

function reflexLabel(value) {
  return labelFromOptions(REFLEX_OPTIONS, value);
}

function buildPersonal(intake) {
  const p = intake.personal || {};
  const header = intake.header || {};
  const allergies = p.allergies || {};
  const tp = p.treating_physician || {};
  const meds = Array.isArray(p.current_medications) ? p.current_medications : [];

  const address = [clean(p.address_line1), clean(p.address_line2)].filter(Boolean).join(', ') ||
    clean(p.address);
  const emergency =
    [clean(p.emergency_contact_name), clean(p.emergency_contact_phone)]
      .filter(Boolean)
      .join(' · ') || clean(p.emergency_contact);

  const allergiesText = allergies.has_allergies
    ? clean(allergies.text) ? escapeHtml(allergies.text) : 'Sí'
    : 'No';

  const medsHtml = meds.length
    ? `<ul class="inline-list">${meds
        .map((m) => {
          const name = clean(m.name);
          const dose = clean(m.dose);
          const txt = [name, dose].filter(Boolean).join(' — ');
          return txt ? `<li>${escapeHtml(txt)}</li>` : '';
        })
        .filter(Boolean)
        .join('')}</ul>`
    : DASH;

  const physician =
    [clean(tp.name), clean(tp.specialty), clean(tp.phone) ? `Tel. ${clean(tp.phone)}` : '']
      .filter(Boolean)
      .join(' · ') || DASH;

  const rows = [
    ['Nombre completo', val(p.full_name)],
    ['Fecha de nacimiento', val(formatDate(p.birth_date))],
    ['Edad', val(p.age)],
    ['Sexo biológico', val(p.biological_sex || p.sex)],
    ['Género', val(p.gender)],
    ['Estado civil', val(labelFromOptions(MARITAL_STATUS_OPTIONS, p.marital_status))],
    ['Escolaridad', val(labelFromOptions(EDUCATION_OPTIONS, p.education))],
    ['CURP', val(p.curp)],
    ['Tipo de sangre', val(p.blood_type)],
    ['Teléfono', val(p.phone)],
    ['Correo', val(p.email)],
    ['Domicilio', val(address)],
    ['Ocupación', val(p.occupation)],
    ['Contacto de emergencia', val(emergency)],
    ['Parentesco (emergencia)', val(p.emergency_contact_relationship)],
    ['Alergias', allergiesText],
    ['Medicamentos actuales', medsHtml],
    ['Médico tratante', physician],
  ];

  const meta = [
    clean(header.folio) ? `<strong>Folio:</strong> ${escapeHtml(header.folio)}` : '',
    clean(header.form_date) ? `<strong>Fecha:</strong> ${escapeHtml(formatDate(header.form_date))}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const metaLine = meta ? `<p class="meta-line">${meta}</p>` : '';

  return section('Datos personales', metaLine + fieldTable(rows));
}

function buildAntecedents(intake) {
  const path = intake.pathological || {};
  const np = intake.non_pathological || {};
  const fam = intake.family_history || {};

  const pathRows = [
    pathologyRow('Cardiovasculares', path.cardiovascular, CARDIOVASCULAR_CONDITIONS),
    pathologyRow('Pulmonares', path.pulmonary),
    pathologyRow('Gastrointestinales', path.gastrointestinal, GASTROINTESTINAL_CONDITIONS),
    pathologyRow('Endocrinos', path.endocrine, ENDOCRINE_CONDITIONS),
    pathologyRow('Dermatológicos', path.dermatological, DERMATOLOGICAL_CONDITIONS),
    pathologyRow('Neurológicos', path.neurological, NEUROLOGICAL_CONDITIONS),
    pathologyRow('Cáncer', path.cancer),
  ];

  const npRows = [
    pathologyRow('Tabaquismo', np.smoking),
    pathologyRow('Alcoholismo', np.alcoholism),
    pathologyRow('Toxicomanías', np.substance_use),
    pathologyRow('Traumatismos', np.trauma),
    pathologyRow('Cirugías', np.surgeries),
  ];

  const footwear = (np.footwear_habits || [])
    .map((id) => labelFromOptions(FOOTWEAR_HABIT_OPTIONS, id) || id)
    .filter(Boolean)
    .join(', ');

  const npExtra = fieldTable([
    ['Actividad física', val(np.physical_activity)],
    ['Higiene podal', val(labelFromOptions(FOOT_HYGIENE_OPTIONS, np.foot_hygiene))],
    ['Calzado habitual', val(footwear)],
  ]);

  const famRows = [
    pathologyRow('Diabetes', fam.diabetes),
    pathologyRow('Hipertensión', fam.hypertension),
    pathologyRow('Enf. vascular periférica', fam.peripheral_vascular),
    pathologyRow('Amputaciones', fam.amputations),
    pathologyRow('Neuropatías', fam.neuropathies),
    pathologyRow('Reumatológicos', fam.rheumatologic),
    pathologyRow('Cáncer', fam.cancer),
    pathologyRow('Crisis convulsivas', fam.seizures),
  ];

  const inner =
    subTitle('Antecedentes patológicos') +
    pathologyTable(pathRows) +
    subTitle('Antecedentes no patológicos') +
    pathologyTable(npRows) +
    npExtra +
    subTitle('Antecedentes heredofamiliares') +
    pathologyTable(famRows);

  return section('Antecedentes', inner);
}

function buildPersonalExtended(intake) {
  const pe = intake.personal_extended || {};
  const symptoms = pe.symptoms || {};
  const activeSymptoms = ADULT_SYMPTOM_OPTIONS.filter(([key]) => symptoms[key])
    .map(([, label]) => label)
    .join(', ');
  const evolution =
    clean(pe.evolution_value)
      ? `${clean(pe.evolution_value)} ${labelFromOptions(EVOLUTION_UNITS, pe.evolution_unit) || ''}`.trim()
      : '';

  const rows = [
    ['Motivo principal de consulta', multilineVal(pe.chief_complaint)],
    ['Dolor (EVA 0-10)', val(pe.pain_eva)],
    ['Tiempo de evolución', val(evolution)],
    ['Síntomas actuales', val(activeSymptoms)],
    ['Terapias o tratamientos previos', multilineVal(pe.prior_therapies)],
    ['Laboratorios / estudios', multilineVal(pe.lab_tests_done)],
    ['Fecha del estudio', val(formatDate(pe.lab_tests_when))],
    ['Resultados', val(pe.lab_tests_results)],
  ];

  return section('Historia personal', fieldTable(rows));
}

function buildPodiatric(intake) {
  const po = intake.podiatric || {};
  const reflexes = po.reflexes || { left: {}, right: {} };
  const pulses = po.pulses || {};
  const sens = po.sensitivity || {};
  const alterations = po.alterations || {};

  const reflexRows = [
    ['Aquíleo', reflexes.left?.achilles, reflexes.right?.achilles],
    ['Rotuliano', reflexes.left?.patellar, reflexes.right?.patellar],
    ['Plantar', reflexes.left?.plantar, reflexes.right?.plantar],
  ]
    .map(
      ([label, l, r]) => `
      <tr>
        <td class="bil-label">${escapeHtml(label)}</td>
        <td>${val(reflexLabel(l))}</td>
        <td>${val(reflexLabel(r))}</td>
      </tr>`
    )
    .join('');

  const reflexTable = `
    <table class="bilateral-table">
      <thead><tr><th>Reflejo</th><th>Izquierdo</th><th>Derecho</th></tr></thead>
      <tbody>${reflexRows}</tbody>
    </table>`;

  const pulsesText =
    [
      pulses.pedio_left ? 'Pedio izq.' : '',
      pulses.pedio_right ? 'Pedio der.' : '',
      pulses.tibial_post_left ? 'Tibial post. izq.' : '',
      pulses.tibial_post_right ? 'Tibial post. der.' : '',
    ]
      .filter(Boolean)
      .join(', ') || '';

  const sensRows = SENSITIVITY_ROWS.map(({ key, label }) => `${label}: ${clean(sens[key]) || DASH}`)
    .join(' · ');

  const altRows = ALTERATION_ZONES.map(({ key, label }) => {
    const entry = alterations[key] || {};
    const desc = clean(entry.description);
    return [`Alteraciones ${label.toLowerCase()}`, val(desc)];
  });

  const rows = [
    ['Tipo de marcha', val(labelFromOptions(GAIT_TYPE_OPTIONS, po.gait_type))],
    ['Huella plantar', val(labelFromOptions(PLANTAR_PRINT_OPTIONS, po.plantar_print))],
    ['Pulsos presentes', val(pulsesText)],
    ['Notas de pulsos', val(po.pulses_notes)],
    ['Temperatura', val(labelFromOptions(TEMPERATURE_OPTIONS, po.temperature) || po.temperature)],
    ['Llenado capilar', val(po.capillary_refill)],
    ['Edema', val(labelFromOptions(EDEMA_OPTIONS, po.edema) || po.edema)],
    ['Sensibilidad', escapeHtml(sensRows)],
    ...altRows,
  ];

  return section('Valoración podológica', fieldTable(rows) + reflexTable);
}

function buildBiomechanical(intake) {
  const bio = intake.biomechanical || {};
  const segments = bio.segments || { right: {}, left: {} };
  const segKeys = [
    ['hip', 'Cadera'],
    ['knee', 'Rodilla'],
    ['hindfoot', 'Retropié'],
    ['forefoot', 'Antepié'],
    ['midfoot', 'Medio pie'],
    ['ankle', 'Tobillo'],
  ];

  const segRows = segKeys
    .map(
      ([k, label]) => `
      <tr>
        <td class="bil-label">${escapeHtml(label)}</td>
        <td>${val(segments.right?.[k])}</td>
        <td>${val(segments.left?.[k])}</td>
      </tr>`
    )
    .join('');

  const segTable = `
    <table class="bilateral-table">
      <thead><tr><th>Segmento</th><th>Derecho</th><th>Izquierdo</th></tr></thead>
      <tbody>${segRows}</tbody>
    </table>`;

  const rows = [
    ['Limitación funcional', val(labelFromOptions(LIMITATION_OPTIONS, bio.limitation))],
    ['Columna', multilineVal(bio.column_spine)],
  ];

  return section('Biomecánica', fieldTable(rows) + segTable);
}

function buildMuscle(intake) {
  const md = intake.muscle_dynamic || {};
  const rows = [
    ['Exploración muscular', multilineVal(md.muscle_exam)],
    ['Exploración dinámica', multilineVal(md.dynamic_exam)],
    ['Tipo de calzado', val(md.footwear)],
  ];
  return section('Exploración muscular y dinámica', fieldTable(rows));
}

function buildDiagnosis(intake) {
  const dt = intake.diagnosis_treatment || {};
  const secondary = Array.isArray(dt.diagnosis_secondary) ? dt.diagnosis_secondary : [];
  const secondaryHtml = secondary.length
    ? `<ul class="inline-list">${secondary
        .map((s) => `<li>${escapeHtml(s)}</li>`)
        .join('')}</ul>`
    : DASH;

  const rows = [
    ['Diagnóstico principal', multilineVal(dt.diagnosis_primary || dt.diagnosis)],
    ['Diagnósticos secundarios', secondaryHtml],
    ['CIE-10', val(dt.cie10)],
    ['Pronóstico', val(labelFromOptions(PROGNOSIS_OPTIONS, dt.prognosis))],
    ['Procedimiento realizado hoy', multilineVal(dt.procedure_today)],
    ['Plan de tratamiento', multilineVal(dt.treatment_plan || dt.orthotic_treatment)],
    ['Indicaciones post-tratamiento', multilineVal(dt.post_care_instructions)],
    ['Próxima cita', val(formatDate(dt.next_appointment))],
  ];

  return section('Diagnóstico y tratamiento', fieldTable(rows));
}

function buildConsent(consent) {
  const c = consent || {};
  if (!c || Object.keys(c).length === 0) return '';
  const photos =
    c.clinical_photos_consent === 'yes'
      ? 'Autoriza fotografías clínicas y de expediente'
      : c.clinical_photos_consent === 'no'
        ? 'No autoriza fotografías clínicas'
        : '';

  const rows = [
    ['Nombre del paciente (firma)', val(c.patient_signature_name)],
    ['Fecha', val(formatDate(c.signed_at))],
    ['Responsable de atención podológica', val(c.podiatrist_name)],
    ['Cédula profesional', val(c.podiatrist_license)],
    ['Fotografías clínicas', val(photos)],
    ['Consentimiento autorizado', boolLabel(c.authorized)],
  ];

  return section('Consentimiento informado', fieldTable(rows));
}

export function buildIntakeRecordHtml({
  logoUrl = '',
  logoDataUrl = '',
  clinicName = 'PodoclinicAM',
  intake = {},
  consent = {},
} = {}) {
  const patientName = clean(intake.personal?.full_name);
  const logoSrc = logoDataUrl || logoUrl;
  const logoBlock = logoSrc
    ? `<img class="logo" src="${escapeAttr(logoSrc)}" alt="${escapeHtml(clinicName)}" />`
    : '';

  const body = `
    <header class="doc-header">
      ${logoBlock}
      <div class="clinic-meta">
        <p class="clinic-name">${escapeHtml(clinicName)}</p>
      </div>
    </header>
    <h1 class="doc-title">HISTORIA CLÍNICA PODOLÓGICA</h1>
    <p class="patient-line"><strong>Paciente:</strong> ${escapeHtml(patientName || DASH)}</p>
    ${buildPersonal(intake)}
    ${buildAntecedents(intake)}
    ${buildPersonalExtended(intake)}
    ${buildPodiatric(intake)}
    ${buildBiomechanical(intake)}
    ${buildMuscle(intake)}
    ${buildDiagnosis(intake)}
    ${buildConsent(consent)}
    <footer class="doc-footer">
      <p class="footer-hint">Documento generado desde el expediente clínico.</p>
    </footer>
  `;

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Historia clínica — ${escapeHtml(patientName || 'Paciente')}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: Georgia, "Times New Roman", serif;
      font-size: 11pt;
      line-height: 1.45;
      color: #111;
      max-width: 210mm;
      margin: 0 auto;
      padding: 16mm 14mm;
      background: #fff;
    }
    .doc-header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 12px;
      margin-bottom: 6mm;
      padding-bottom: 4mm;
      border-bottom: 1px solid #ccc;
    }
    .logo { max-height: 22mm; max-width: 55mm; object-fit: contain; }
    .clinic-meta { flex: 1; min-width: 140px; font-family: system-ui, sans-serif; }
    .clinic-name { font-weight: 700; font-size: 14pt; margin: 0; }
    .doc-title {
      font-family: system-ui, sans-serif;
      font-size: 14pt;
      text-align: center;
      margin: 0 0 4mm;
      letter-spacing: 0.04em;
    }
    .patient-line {
      font-family: system-ui, sans-serif;
      font-size: 11pt;
      margin: 0 0 5mm;
    }
    .doc-section { margin: 0 0 6mm; page-break-inside: avoid; }
    .section-title {
      font-family: system-ui, sans-serif;
      font-size: 11.5pt;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: #1a4d3a;
      border-bottom: 1px solid #1a4d3a;
      padding-bottom: 1.5mm;
      margin: 0 0 3mm;
    }
    .sub-title {
      font-family: system-ui, sans-serif;
      font-size: 10pt;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      color: #444;
      margin: 4mm 0 2mm;
    }
    .field-table, .bilateral-table {
      width: 100%;
      border-collapse: collapse;
      margin: 0 0 3mm;
      font-family: system-ui, sans-serif;
      font-size: 10pt;
    }
    .field-table th, .field-table td,
    .bilateral-table th, .bilateral-table td {
      border: 1px solid #ccc;
      padding: 2mm 3mm;
      text-align: left;
      vertical-align: top;
    }
    .field-label {
      width: 38%;
      background: #f3f6f4;
      font-weight: 600;
      color: #333;
    }
    .field-value { white-space: normal; }
    .bilateral-table th {
      background: #f3f6f4;
      font-weight: 600;
      text-align: center;
      color: #333;
    }
    .bilateral-table td { text-align: center; }
    .bilateral-table .bil-label { text-align: left; font-weight: 600; background: #fafbfa; }
    .inline-list { margin: 0; padding-left: 5mm; }
    .muted { color: #666; font-size: 9pt; }
    .meta-line { font-family: system-ui, sans-serif; font-size: 10pt; color: #444; margin: 0 0 2mm; }
    .doc-footer {
      margin-top: 8mm;
      padding-top: 3mm;
      border-top: 1px solid #ccc;
      font-family: system-ui, sans-serif;
      font-size: 9pt;
      color: #666;
    }
    .footer-hint { margin: 0; }
    .toolbar {
      position: fixed;
      top: 0; left: 0; right: 0;
      background: #1a4d3a;
      color: #fff;
      padding: 10px 16px;
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      align-items: center;
      z-index: 100;
      font-family: system-ui, sans-serif;
      font-size: 14px;
    }
    .toolbar button {
      background: #fff;
      color: #1a4d3a;
      border: none;
      padding: 8px 16px;
      border-radius: 999px;
      font-weight: 600;
      cursor: pointer;
    }
    .toolbar button:hover { background: #e8f5ee; }
    .toolbar-hint { opacity: 0.9; font-size: 12px; flex: 1; min-width: 200px; }
    body.has-toolbar { padding-top: 56px; }
    @media print {
      .toolbar { display: none !important; }
      body.has-toolbar { padding-top: 16mm; }
    }
  </style>
</head>
<body class="has-toolbar">
  <div class="toolbar">
    <button type="button" onclick="window.print()">Imprimir / Guardar PDF</button>
    <button type="button" onclick="window.close()">Cerrar</button>
    <span class="toolbar-hint">En el diálogo de impresión elija «Guardar como PDF» o imprima en papel.</span>
  </div>
  <div class="doc-content">${body}</div>
</body>
</html>`;
}

export function openIntakeRecordPreview(payload, { autoPrint = false } = {}) {
  const html = buildIntakeRecordHtml(payload);
  const serialized = JSON.stringify(payload);
  try {
    localStorage.setItem(INTAKE_RECORD_PREVIEW_STORAGE_KEY, serialized);
    sessionStorage.setItem(INTAKE_RECORD_PREVIEW_STORAGE_KEY, serialized);
  } catch {
    /* ignore */
  }
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);
  const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
  if (!win) {
    URL.revokeObjectURL(blobUrl);
    return { ok: false, reason: 'popup_blocked' };
  }
  win.addEventListener('load', () => {
    URL.revokeObjectURL(blobUrl);
    if (autoPrint) {
      try {
        win.print();
      } catch {
        /* ignore */
      }
    }
  });
  return { ok: true };
}

export function readIntakeRecordPreviewPayload() {
  try {
    const raw =
      localStorage.getItem(INTAKE_RECORD_PREVIEW_STORAGE_KEY) ||
      sessionStorage.getItem(INTAKE_RECORD_PREVIEW_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
